import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { getCategoryName, paginate, searchMovies, sortMovies } from "@/utils/movie";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 12;
const YEAR_OPTIONS = ["全部年份", "2024", "2023", "2022", "2021"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";

  const [input, setInput] = useState(query);
  const [category, setCategory] = useState("全部");
  const [year, setYear] = useState("全部年份");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  useSeo({
    title: query
      ? `"${query}" 的搜索结果 - 红桃视频在线观看`
      : "搜索影视 - 红桃视频在线观看",
    description: query
      ? `红桃视频在线观看为你搜索到与"${query}"相关的影视作品，支持按类别与年份进一步筛选。`
      : "在红桃视频在线观看搜索影视作品，支持片名、导演、演员与类型关键词模糊匹配。",
    keywords: "影视搜索,红桃视频在线观看,在线观看,电影搜索,电视剧搜索",
    canonicalPath: query ? `/search?q=${encodeURIComponent(query)}` : "/search",
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
    setPage(1);
  };

  const matched = useMemo(() => {
    if (!query.trim()) return [];
    return searchMovies(movies, query);
  }, [query]);

  const filtered = useMemo(() => {
    return matched.filter((m) => {
      if (category !== "全部" && m.category !== category) return false;
      if (year !== "全部年份" && String(m.year) !== year) return false;
      return true;
    });
  }, [matched, category, year]);

  const sorted = useMemo(() => sortMovies(filtered, "popularity"), [filtered]);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageItems = paginate(sorted, page, PAGE_SIZE);

  const popular = useMemo(() => sortMovies(movies, "popularity").slice(0, 6), []);

  const chipClass = (active: boolean) =>
    `cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
    }`;

  const categoryTabs = [{ key: "全部", name: "全部" }, ...categories.map((c) => ({ key: c.key, name: c.name }))];

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground-950 md:text-3xl">
          <span className="flex h-8 w-8 items-center justify-center text-primary-500">
            <i className="ri-search-2-line text-2xl" />
          </span>
          搜索影视
        </h1>

        <form
          onSubmit={handleSubmit}
          className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-search-line text-base text-foreground-500" />
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            name="q"
            placeholder="输入片名、导演、演员或类型关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </header>

      {query.trim() ? (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <span>当前搜索词：</span>
            <span className="rounded-md bg-primary-50 px-2.5 py-1 font-medium text-primary-600">
              {query}
            </span>
            <span className="text-foreground-500">
              共找到 <span className="font-medium text-primary-600">{sorted.length}</span> 个结果
            </span>
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-foreground-800">类别</span>
              <div className="flex flex-wrap gap-2">
                {categoryTabs.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => {
                      setCategory(c.key);
                      setPage(1);
                    }}
                    className={chipClass(category === c.key)}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
              <span className="text-sm font-medium text-foreground-800">年份</span>
              <div className="flex flex-wrap gap-2">
                {YEAR_OPTIONS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      setYear(y);
                      setPage(1);
                    }}
                    className={chipClass(year === y)}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <div className="mt-10">
                <Pagination currentPage={page} totalPages={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="mt-10">
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <h3 className="mt-5 text-lg font-medium text-foreground-950">
                  没有找到与“{query}”相关的影视
                </h3>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  换个关键词试试，或者看看下面的热门推荐吧。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCategory("全部");
                      setYear("全部年份");
                      setInput("");
                      setParams({});
                    }}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重新搜索
                  </button>
                  <Link
                    to="/"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              </div>

              <div className="mt-12">
                <SectionHeading title="热门推荐" icon="ri-fire-line" moreTo="/" moreLabel="回到首页" />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                  {popular.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-12">
          <SectionHeading
            title="大家都在看"
            subtitle="热门影视推荐，点击卡片即可查看详情"
            icon="ri-fire-line"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {popular.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            <span className="text-sm text-foreground-500">热门关键词：</span>
            {["悬疑", "科幻", "喜剧", "爱情", "动作", "美食"].map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => {
                  setInput(kw);
                  setParams({ q: kw });
                }}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 md:text-sm"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}